import {
  KeyboardEvent,
  ReactNode,
  SyntheticEvent,
  useEffect,
  useState,
} from 'react';

import ClearIcon from '@mui/icons-material/Clear';
import {
  Autocomplete,
  Box,
  InputLabel,
  TextField,
  Button,
  Stack,
} from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';

import { VehicleApi } from '@/api';
import { IcArrowDown } from '@/assets/images';
import { AutoCompleteRoot } from '@/components/FormElements/Autocomplete/styles';
import { IVehicleMake } from '@/models';
import { useToastStore } from '@/store';

interface IVehicleMakeAutoCompleteProps {
  excludeVehicleMakes?: Array<IVehicleMake>;
  noResults?: ReactNode;
  disabled?: boolean;
  label: string;
  value?: string; // Made optional
  onChange: (value: { name: string; _id?: string }) => void;
  onKeyUp?: (e: KeyboardEvent) => void;
  sx?: any;
  vehicleType?: string;
}

export const VehicleMakeAutocomplete = ({
  excludeVehicleMakes = [],
  noResults,
  onChange,
  value,
  label,
  onKeyUp,
  sx,
  disabled = false,
  vehicleType,
  ...restProps
}: IVehicleMakeAutoCompleteProps) => {
  const [searchText, setSearchText] = useState(String(value || ''));
  const [vehicleMakes, setVehicleMakes] = useState<Array<IVehicleMake>>([]);
  const [selectedMake, setSelectedMake] = useState<IVehicleMake | null>(null);

  const { updateToast } = useToastStore();

  // Only update searchText when component first mounts or when value is empty
  useEffect(() => {
    if (!searchText && value) {
      setSearchText(String(value));
    }
  }, [value]);

  useEffect(() => {
    fetchVehicleMakes();
  }, []);

  const fetchVehicleMakes = async (search?: string) => {
    try {
      const res = await VehicleApi.getVehicleMakeOptions({
        search,
        vehicleType,
      });
      setVehicleMakes(res.data);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const debouncedFetchVehicleMakes = useDebouncedCallback((search: string) => {
    fetchVehicleMakes(search);
  }, 500);

  const handleInputChange = (_: SyntheticEvent, v: string) => {
    setSearchText(v);
    debouncedFetchVehicleMakes(v);
  };

  const handleCreateCustomMake = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!searchText) return;

    // Add the new make to local state so it appears in the list
    const newMake: IVehicleMake = {
      _id: `custom-${Date.now()}`, // Temporary ID
      name: searchText,
      id: 0,
      externalId: Date.now(),
    };

    setVehicleMakes((prev) => [...prev, newMake]);
    setSelectedMake(newMake);
    onChange({ name: searchText }); // No _id means it's a custom value
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && searchText) {
      e.preventDefault();

      // Check if the current input matches an API option
      const existingMake = vehicleMakes.find(
        (make) => make.name.toLowerCase() === searchText.toLowerCase(),
      );

      if (existingMake) {
        // Use the API option
        setSelectedMake(existingMake);
        onChange({ name: existingMake.name, _id: existingMake._id });
      } else {
        // Create custom value
        handleCreateCustomMake();
      }
    }
  };

  const getDisplayValue = () => {
    if (selectedMake) {
      return selectedMake;
    }
    // For custom string values, create a temporary object for display
    if (searchText) {
      return { name: searchText, _id: '', id: 0, externalId: 0 };
    }
    return null;
  };

  const showCreateOption =
    searchText &&
    typeof searchText === 'string' &&
    searchText.trim() &&
    !vehicleMakes.some(
      (make) => make.name.toLowerCase() === searchText.toLowerCase(),
    );

  return (
    <AutoCompleteRoot sx={sx} {...restProps}>
      {!!label && <InputLabel>{label}</InputLabel>}

      <Autocomplete
        inputValue={searchText}
        value={getDisplayValue()}
        autoHighlight
        popupIcon={<IcArrowDown />}
        fullWidth
        disabled={disabled}
        onInputChange={handleInputChange}
        options={vehicleMakes}
        freeSolo={true} // Allow free text input
        noOptionsText={noResults}
        filterOptions={(allVehicleMakes) => {
          const filteredByExcluded = allVehicleMakes.reduce((acc, item) => {
            const found = (excludeVehicleMakes || []).find((excludedItem) => {
              return excludedItem?._id === item._id;
            });
            return found ? acc : [...acc, item];
          }, [] as Array<IVehicleMake>);

          // Add create option if search text doesn't match any existing option
          if (showCreateOption) {
            filteredByExcluded.push({
              _id: 'create-new',
              name: `+ Create "${searchText}" as a new item`,
              id: 0,
              externalId: 0,
            } as IVehicleMake);
          }

          return filteredByExcluded;
        }}
        onChange={(_, selectedOption) => {
          if (selectedOption) {
            if (typeof selectedOption === 'string') {
              // Free text input
              setSelectedMake(null);
              setSearchText(selectedOption);
              onChange({ name: selectedOption });
            } else if (selectedOption._id === 'create-new') {
              // Create new option selected
              handleCreateCustomMake();
            } else {
              // API option selected
              setSelectedMake(selectedOption);
              setSearchText(selectedOption.name);
              onChange({ name: selectedOption.name, _id: selectedOption._id });
            }
          }
        }}
        getOptionLabel={(option) => {
          if (typeof option === 'string') {
            return option;
          }
          return option.name;
        }}
        isOptionEqualToValue={(option, selectedValue) => {
          if (typeof option === 'string' && typeof selectedValue === 'string') {
            return option === selectedValue;
          }
          if (typeof option === 'object' && typeof selectedValue === 'object') {
            return option._id === selectedValue._id;
          }
          return false;
        }}
        renderOption={(props, option) => {
          if (option._id === 'create-new') {
            return (
              <Stack
                component="li"
                {...props}
                key={option._id}
                justifyContent="flex-end"
                width="100%"
                sx={{ m: 1 }}
              >
                <Button
                  onClick={handleCreateCustomMake}
                  variant="text"
                  color="error"
                  sx={{ justifyContent: 'flex-start', width: '100%' }}
                >
                  {option.name}
                </Button>
              </Stack>
            );
          }

          return (
            <Box
              component="li"
              {...props}
              key={option._id}
              flexDirection="column"
              flexWrap="wrap"
              sx={{ m: 1 }}
            >
              <p style={{ width: '100%', margin: 0 }}>{option.name}</p>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            value={searchText}
            placeholder="Make"
            onKeyUp={(e) => {
              if (onKeyUp) {
                onKeyUp(e);
              }
            }}
            onKeyDown={handleKeyDown}
            fullWidth
          />
        )}
        clearOnBlur={false}
        clearIcon={<ClearIcon sx={{ width: 20, height: 20 }} />}
        disableClearable={false}
      />
    </AutoCompleteRoot>
  );
};
